import '@milkdown/crepe/theme/common/style.css'
import '@milkdown/crepe/theme/nord.css'
import '@/styles/MarkdownEditor.css'

import { Alert, Textarea } from '@mantine/core'
import { Crepe } from '@milkdown/crepe'
import { linkAttr } from '@milkdown/kit/preset/commonmark'
import type { Mark } from '@milkdown/kit/prose/model'
import { replaceAll } from '@milkdown/utils'
import { IconAlertTriangle } from '@tabler/icons-react'
import React, { useEffect, useEffectEvent, useRef, useState } from 'react'

import { transcriptHasSpeakerLabels } from '@/utils/transcript'

// Speaker tags are stored as markdown links `[Name](spk_0)` so the `spk_N`
// identity survives for re-identification. In the WYSIWYG editor a raw link
// renders as a clickable hyperlink; decorate `spk_` links so they present as a
// non-clickable "Name (speaker 0)" chip instead. Serialization is untouched, so
// the underlying `[Name](spk_0)` markdown is preserved on save.
const SPEAKER_HREF_RE = /^spk_(\d+)$/

export interface MarkdownEditorProps {
  /** Initial markdown content */
  defaultValue?: string
  /** Current markdown content for controlled usage */
  value?: string
  /** Called when content changes */
  onChange?: (markdown: string) => void
  /** Whether the editor is read-only */
  readOnly?: boolean
  /** CSS class name */
  className?: string
  /** Inline styles */
  style?: React.CSSProperties
}

/**
 * MarkdownEditor component with fallback support
 *
 * This component uses Milkdown/Crepe for rich markdown editing, but gracefully
 * falls back to a textarea if the editor fails to initialize.
 *
 * States:
 * - 'loading': Initial state while editor initializes
 * - 'ready': Editor loaded successfully
 * - 'error': Editor failed to load, shows fallback textarea with alert
 */

export function MarkdownEditor(props: MarkdownEditorProps) {
  const { defaultValue = '', value, onChange, readOnly = false, className, style } = props

  const containerRef = useRef<HTMLDivElement | null>(null)
  const crepeRef = useRef<Crepe | null>(null)
  const currentValueRef = useRef<string>(value ?? defaultValue)
  const onChangeRef = useRef(onChange)

  // Track editor state for fallback
  const [editorState, setEditorState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [fallbackValue, setFallbackValue] = useState<string>(value ?? defaultValue)

  // Defer heavy Crepe initialization so the hosting modal/container can paint first.
  const [readyToInit, setReadyToInit] = useState(false)

  useEffect(() => {
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(() => setReadyToInit(true))
      return () => cancelIdleCallback(id)
    }
    const id = setTimeout(() => setReadyToInit(true), 40)
    return () => clearTimeout(id)
  }, [])

  // Keep onChange callback current
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Handle fallback textarea changes
  const handleFallbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    setFallbackValue(newValue)
    currentValueRef.current = newValue
    onChangeRef.current?.(newValue)
  }

  const getInitialProps = useEffectEvent(() => ({ initialContent: value ?? defaultValue, initialReadOnly: readOnly }))

  // Initialize Crepe editor (deferred until readyToInit)
  useEffect(() => {
    if (!readyToInit || !containerRef.current) return

    const { initialContent, initialReadOnly } = getInitialProps()
    const crepe = new Crepe({
      root: containerRef.current,
      defaultValue: initialContent,
      // A speaker transcript's only links are `[Name](spk_0)` tags. The link
      // tooltip is hover-triggered off the document model (ignores CSS), so it
      // would still surface the raw `spk_0` href — disable it for transcripts.
      features: transcriptHasSpeakerLabels(initialContent) ? { [Crepe.Feature.LinkTooltip]: false } : undefined,
    })

    crepe.editor.config(ctx => {
      ctx.update(linkAttr.key, prev => (mark: Mark) => {
        const base = prev(mark)
        const href = typeof mark.attrs.href === 'string' ? mark.attrs.href : ''
        const match = SPEAKER_HREF_RE.exec(href)
        if (!match) return base
        return { ...base, class: 'speaker-tag', 'data-speaker': `speaker ${match[1]}` }
      })
    })

    crepeRef.current = crepe
    currentValueRef.current = initialContent

    crepe
      .on(listener => {
        listener.markdownUpdated((_, markdown) => {
          currentValueRef.current = markdown
          onChangeRef.current?.(markdown)
        })
      })
      .create()
      .then(() => {
        crepe.setReadonly(initialReadOnly)
        setEditorState('ready')
      })
      .catch(error => {
        console.error('Failed to initialize markdown editor:', error)
        setEditorState('error')
        setFallbackValue(currentValueRef.current)
      })

    return () => {
      try {
        crepe.destroy()
      } catch (error) {
        console.warn('Error destroying markdown editor:', error)
      }
      crepeRef.current = null
    }
  }, [readyToInit]) // Only run once readyToInit flips to true

  if (editorState === 'error' && value !== undefined && value !== fallbackValue) {
    setFallbackValue(value)
  }

  // Handle controlled value changes
  useEffect(() => {
    if (editorState === 'error') return

    const crepe = crepeRef.current
    if (!crepe || value === undefined) return

    if (value !== currentValueRef.current) {
      crepe.editor.action(replaceAll(value))
      currentValueRef.current = value
    }
  }, [value, editorState])

  // Handle readOnly changes
  useEffect(() => {
    if (editorState !== 'ready') return

    const crepe = crepeRef.current
    if (!crepe) return
    crepe.setReadonly(readOnly)
  }, [readOnly, editorState])

  // Show fallback textarea if editor failed to load
  if (editorState === 'error') {
    return (
      <div className={className} style={style}>
        <Alert
          icon={<IconAlertTriangle size="1rem" />}
          title="Markdown Editor Unavailable"
          color="yellow"
          variant="light"
          mb="md">
          The rich text editor failed to load. You can still edit your content using the text area below.
        </Alert>
        <Textarea
          value={fallbackValue}
          onChange={handleFallbackChange}
          disabled={readOnly}
          placeholder="Enter your markdown content here..."
          autosize
          minRows={10}
          resize="vertical"
          style={{ minHeight: '300px' }}
        />
      </div>
    )
  }

  return (
    <div className={className} style={style}>
      {editorState === 'loading' && (
        <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading editor...
        </div>
      )}
      <div
        ref={containerRef}
        className="markdown-content"
        style={{ display: editorState === 'ready' ? 'block' : 'none' }}
      />
    </div>
  )
}
